
import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast";
import { formatDistanceToNow } from "date-fns";
import { useParams } from "react-router-dom";
import { useTributes } from "@/hooks/useTributes";
import { Loader2, Camera, X, Heart, MessageCircle, ArrowLeft } from "lucide-react";

export const CommunitySection = () => {
  const [message, setMessage] = React.useState("");
  const [author, setAuthor] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [selectedPhoto, setSelectedPhoto] = React.useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = React.useState<string | null>(null);
  const [honeypot, setHoneypot] = React.useState(""); // Anti-spam honeypot
  const [showForm, setShowForm] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const {
    id: memorialId
  } = useParams<{
    id: string;
  }>();
  const {
    tributes,
    isLoading,
    addTribute
  } = useTributes(memorialId || "");

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast({
          title: "File too large",
          description: "Please select an image smaller than 10MB.",
          variant: "destructive"
        });
        return;
      }
      setSelectedPhoto(file);
      const reader = new FileReader();
      reader.onload = (e) => setPhotoPreview(e.target?.result as string);
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const removePhoto = () => {
    setSelectedPhoto(null);
    setPhotoPreview(null);
  };

  const handlePhotoButtonClick = () => {
    fileInputRef.current?.click();
  };

  const resetForm = () => {
    setShowForm(false);
    setAuthor("");
    setMessage("");
    setSelectedPhoto(null);
    setPhotoPreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot check - bots fill this hidden field, humans don't
    if (honeypot) {
      toast({
        title: "Tribute Posted",
        description: "Your tribute has been shared with the community."
      });
      return; // Silently reject bot submissions
    }

    if (!message.trim() || !author.trim()) {
      toast({
        title: "Error",
        description: "Please enter both your name and message.",
        variant: "destructive"
      });
      return;
    }
    setIsSubmitting(true);
    try {
      await addTribute(author.trim(), message.trim(), selectedPhoto);
      toast({
        title: "Tribute Posted",
        description: "Your tribute has been shared with the community."
      });
      resetForm();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to post tribute. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Share Your Memories — always visible */}
      <div className="bg-card rounded-xl border p-6 shadow-sm">
        <div className="flex flex-col items-center justify-center py-8 px-6 text-center">
          <div className="relative mb-6">
            <div className="absolute inset-0 bg-primary/10 rounded-full blur-xl"></div>
            <div className="relative bg-gradient-to-br from-primary/20 to-primary/10 rounded-full p-6">
              <Heart className="h-12 w-12 text-primary mx-auto" />
            </div>
          </div>

          <h3 className="text-xl font-semibold text-foreground mb-3">
            Share Your Memories
          </h3>

          <p className="text-muted-foreground text-base leading-relaxed max-w-md mb-8">
            Tributes are a beautiful way to honor and remember a life well-lived. Share your favorite memories,
            stories, and thoughts to celebrate their legacy with others.
          </p>

          <Button
            onClick={() => setShowForm(!showForm)}
            className="gap-2"
          >
            <MessageCircle className="h-4 w-4" />
            Write a Tribute
          </Button>
        </div>
      </div>

      {/* Tribute form */}
      {showForm && (
        <div className="bg-card rounded-xl border p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Button variant="ghost" size="sm" className="p-1 h-auto" onClick={resetForm}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h3 className="text-lg font-medium text-foreground">Write a Tribute</h3>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Honeypot field - invisible to humans, bots fill it */}
            <input
              type="text"
              name="website"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              className="absolute -left-[9999px]"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />
            <Input
              type="text"
              placeholder="Your Name"
              value={author}
              onChange={e => setAuthor(e.target.value)}
              className="w-full"
              autoFocus
            />
            <Textarea
              placeholder="Share your memories and tributes..."
              value={message}
              onChange={e => setMessage(e.target.value)}
              className="min-h-[120px]"
            />

            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoSelect}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={handlePhotoButtonClick}
                disabled={isSubmitting}
              >
                <Camera className="h-4 w-4" />
                Add Photo
              </Button>
              {photoPreview && (
                <div className="relative">
                  <img src={photoPreview} alt="Preview" className="h-16 w-16 object-cover rounded-md" />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                    onClick={removePhoto}
                    disabled={isSubmitting}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Post Tribute
            </Button>
          </form>
        </div>
      )}

      {/* Tributes list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : tributes.length > 0 && (
        <div className="space-y-6">
          {tributes.map(tribute => (
            <div key={tribute.id} className="p-6 rounded-xl bg-card shadow-sm border">
              <div className="flex justify-between items-center mb-3 flex-wrap sm:flex-nowrap gap-2">
                <h4 className="font-medium text-foreground text-sm sm:text-base truncate max-w-[60%] sm:max-w-none">
                  {tribute.author}
                </h4>
                <span className="text-xs sm:text-sm text-muted-foreground whitespace-nowrap">
                  {formatDistanceToNow(new Date(tribute.created_at), {
                    addSuffix: true
                  })}
                </span>
              </div>
              <p className="text-foreground text-sm sm:text-base leading-relaxed mb-3">
                {tribute.content}
              </p>
              {tribute.photo_url && (
                <img
                  src={tribute.photo_url}
                  alt="Tribute photo"
                  className="w-full max-w-md h-auto rounded-lg mt-3"
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
